<div class="editor-root">

  <!-- ── Header ─────────────────────────────────────────────────────────── -->
  <div class="editor-header">
    <div class="header-left">
      <button mat-icon-button (click)="goBack()" matTooltip="Back to Designs">
        <mat-icon>arrow_back</mat-icon>
      </button>
      <input class="design-name-input" [(ngModel)]="designName" placeholder="Design name" spellcheck="false"/>
    </div>

    <div class="header-center">
      <button mat-icon-button (click)="undo()" [disabled]="!canUndo" matTooltip="Undo (Ctrl+Z)">
        <mat-icon>undo</mat-icon>
      </button>
      <button mat-icon-button (click)="redo()" [disabled]="!canRedo" matTooltip="Redo (Ctrl+Y)">
        <mat-icon>redo</mat-icon>
      </button>

      <div class="zoom-control">
        <button mat-icon-button (click)="zoomOut()" matTooltip="Zoom Out">
          <mat-icon>remove</mat-icon>
        </button>
        <span class="zoom-label" (click)="resetZoom()">{{zoom}}%</span>
        <button mat-icon-button (click)="zoomIn()" matTooltip="Zoom In">
          <mat-icon>add</mat-icon>
        </button>
      </div>

      <button mat-icon-button (click)="toggleGrid()" [class.active-icon]="showGrid" matTooltip="Toggle Grid">
        <mat-icon>grid_on</mat-icon>
      </button>

      <button mat-icon-button (click)="snapEnabled=!snapEnabled" [class.active-icon]="snapEnabled" matTooltip="Snap to Grid">
        <mat-icon>grid_4x4</mat-icon>
      </button>
    </div>

    <div class="header-right">
      <div class="scale-setting">
        <span class="scale-lbl">Scale:</span>
        <input type="number" class="scale-input" [(ngModel)]="scalePerCell" min="0.1" step="0.1"/>
        <select class="scale-select" [(ngModel)]="scaleUnit">
          <option value="ft">ft / cell</option>
          <option value="m">m / cell</option>
        </select>
      </div>

      <span class="saved-badge" *ngIf="saved">
        <mat-icon style="font-size:16px;height:16px;width:16px;">check_circle</mat-icon> Saved
      </span>
      <button mat-icon-button matTooltip="Export PNG" (click)="exportPng()">
        <mat-icon>download</mat-icon>
      </button>
      <button mat-stroked-button color="primary" (click)="save()" [disabled]="saving" class="save-btn">
        <mat-icon *ngIf="!saving">save</mat-icon>
        <mat-icon *ngIf="saving" class="spin">autorenew</mat-icon>
        {{saving ? 'Saving…' : 'Save'}}
      </button>
    </div>
  </div>

  <!-- ── Body ───────────────────────────────────────────────────────────── -->
  <div class="editor-body">

    <!-- Left toolbar -->
    <div class="toolbar">
      <div class="tool-group">
        <button
          *ngFor="let t of drawingTools"
          class="tool-btn"
          [class.active]="activeTool === t.id"
          (click)="setTool(t.id)"
          [matTooltip]="t.label"
          matTooltipPosition="right">
          <mat-icon>{{t.icon}}</mat-icon>
        </button>
      </div>

      <div class="toolbar-sep"></div>

      <!-- Wall thickness -->
      <div class="prop-item" matTooltip="Wall thickness (px)" matTooltipPosition="right">
        <mat-icon style="font-size:14px;color:#64748b;height:14px;width:14px;">line_weight</mat-icon>
        <input type="number" class="wall-input" [(ngModel)]="wallThickness" min="4" max="80" step="2"/>
        <span class="fill-label">wall px</span>
      </div>

      <div class="toolbar-sep"></div>

      <!-- Stroke color -->
      <div class="prop-item" matTooltip="Stroke / Text color" matTooltipPosition="right">
        <label class="color-swatch">
          <div class="swatch-preview" [style.background]="strokeColor"></div>
          <input type="color" [(ngModel)]="strokeColor" (change)="onColorChange()"/>
        </label>
        <span class="fill-label">Stroke</span>
      </div>

      <!-- Stroke width -->
      <div class="prop-item" matTooltip="Stroke width" matTooltipPosition="right">
        <input
          type="range" class="width-slider"
          [(ngModel)]="strokeWidth" min="1" max="20" step="1"
          (input)="onWidthChange()"/>
        <span class="width-label">{{strokeWidth}}px</span>
      </div>

      <div class="toolbar-sep"></div>

      <!-- Layer controls -->
      <button class="tool-btn" (click)="bringForward()"  matTooltip="Bring Forward"  matTooltipPosition="right">
        <mat-icon>flip_to_front</mat-icon>
      </button>
      <button class="tool-btn" (click)="sendBackward()" matTooltip="Send Backward" matTooltipPosition="right">
        <mat-icon>flip_to_back</mat-icon>
      </button>

      <div class="toolbar-sep"></div>

      <!-- Background -->
      <button class="tool-btn" matTooltip="Upload background image" matTooltipPosition="right"
              (click)="bgInput.click()">
        <mat-icon>image</mat-icon>
      </button>
      <input #bgInput type="file" accept="image/*" style="display:none" (change)="uploadBackground($event)"/>

      <button class="tool-btn" (click)="removeBackground()" matTooltip="Remove background" matTooltipPosition="right">
        <mat-icon>hide_image</mat-icon>
      </button>

      <div class="toolbar-sep"></div>

      <!-- Delete / Clear -->
      <button class="tool-btn del-btn" (click)="deleteSelected()" matTooltip="Delete selected (Del)" matTooltipPosition="right">
        <mat-icon>delete</mat-icon>
      </button>
      <button class="tool-btn clear-btn" (click)="clearAll()" matTooltip="Clear canvas" matTooltipPosition="right">
        <mat-icon>delete_sweep</mat-icon>
      </button>
    </div>

    <!-- Canvas -->
    <div class="canvas-wrapper" #canvasWrapper>
      <div class="loading-overlay" *ngIf="loading">
        <mat-progress-spinner mode="indeterminate" [diameter]="40"></mat-progress-spinner>
      </div>
      <canvas #canvasEl></canvas>
    </div>

    <!-- Right symbol panel -->
    <div class="symbol-panel" [class.collapsed]="!symbolPanelOpen">
      <div class="symbol-panel-header" (click)="symbolPanelOpen=!symbolPanelOpen">
        <span class="panel-title" *ngIf="symbolPanelOpen">
          <mat-icon>library_add</mat-icon>
          Symbols
        </span>
        <mat-icon class="chevron" [class.rotated]="!symbolPanelOpen">chevron_right</mat-icon>
      </div>

      <div class="symbol-panel-body" *ngIf="symbolPanelOpen">
        <mat-accordion multi="true">
          <mat-expansion-panel
            *ngFor="let cat of symbolCategories"
            [(expanded)]="cat.open"
            class="sym-cat-panel">

            <mat-expansion-panel-header class="sym-cat-header">
              <mat-panel-title class="sym-cat-title">
                <mat-icon class="sym-cat-icon">{{cat.icon}}</mat-icon>
                {{cat.label}}
              </mat-panel-title>
            </mat-expansion-panel-header>

            <div class="sym-grid">
              <div
                *ngFor="let sym of cat.symbols"
                class="sym-item"
                (click)="placeSymbol(sym)"
                [matTooltip]="'Place: ' + sym.label"
                matTooltipPosition="left">
                <div class="sym-preview">
                  <img [src]="getSymbolSrc(sym.id)" [alt]="sym.label" draggable="false"/>
                </div>
                <span class="sym-label">{{sym.label}}</span>
              </div>
            </div>

          </mat-expansion-panel>
        </mat-accordion>
      </div>
    </div>

  </div><!-- /editor-body -->

  <!-- ── Status bar ─────────────────────────────────────────────────────── -->
  <div class="status-bar">
    <span class="status-item">
      <mat-icon class="status-icon">place</mat-icon>
      X: {{cursorX | number:'1.1-1'}} {{scaleUnit}} &nbsp; Y: {{cursorY | number:'1.1-1'}} {{scaleUnit}}
    </span>
    <span class="status-sep">|</span>
    <span class="status-item">
      <mat-icon class="status-icon">aspect_ratio</mat-icon>
      Scale: 1 cell = {{scalePerCell}} {{scaleUnit}}
    </span>
    <span class="status-sep">|</span>
    <span class="status-item" [class.snap-on]="snapEnabled">
      <mat-icon class="status-icon">grid_4x4</mat-icon>
      Snap {{snapEnabled ? 'ON' : 'OFF'}}
    </span>
    <span class="status-sep" *ngIf="activeTool==='dim'">|</span>
    <span class="status-item hint" *ngIf="activeTool==='dim' && dimStart">
      Click second point to finish dimension
    </span>
    <span class="status-item hint" *ngIf="activeTool==='dim' && !dimStart">
      Click first point to start dimension
    </span>
    <span class="status-right">
      <input class="desc-input" [(ngModel)]="designDesc" placeholder="Add a description…"/>
    </span>
  </div>

</div>
